import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useFormContext } from "react-hook-form";
import axiosInstance from "../../services/api/axios";
import { useUser } from "../../context/UserContext";
import {
  FormModal,
  Form,
  Field,
  FormSubmit,
} from "../../components/form";
import { OutlineButton } from "../../components/Buttons";
import { fetchPropertyConfigurations } from "../../services/projects";
import { scheduleVisit } from "../../services/users";

/**
 * Manual lead upload — clean replacement for the bespoke 3-step
 * <ScheduleVisit flowType="rawLead"> wizard. Single form, validated by yup,
 * driven by react-hook-form via the canonical Form / Field primitives.
 *
 *   Required: project, firstName, lastName, mobileNoWa, mobileNoCalling, emailId
 *   Optional: propertyConfig, preferredDate + preferredTime (or walkIn)
 *
 * On submit:
 *   1. POST /users  → creates the lead's user record
 *   2. PUT  /customers/user (when propertyConfig set) → links the config
 *   3. scheduleVisit() (when visit date+time set OR walkIn checked) → schedules
 *      or records the walk-in
 *
 * Same chain as the legacy flow; the difference is the form is one screen,
 * validated, and uses the unified primitives.
 */
const E164_OR_FREEFORM_PHONE = /^\+?[0-9][0-9\s\-()]{6,}$/;

const manualLeadSchema = yup.object({
  projectId: yup.string().trim().required("Project is required"),
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  mobileNoWa: yup
    .string()
    .trim()
    .required("WhatsApp number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  mobileNoCalling: yup
    .string()
    .trim()
    .required("Calling number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  emailId: yup
    .string()
    .trim()
    .required("Email is required")
    .email("Enter a valid email"),
  propertyConfig: yup.string().trim().nullable(),
  walkIn: yup.boolean().default(false),
  preferredDate: yup.string().trim().nullable(),
  preferredTime: yup.string().trim().nullable(),
});

const defaultValues = {
  projectId: "",
  firstName: "",
  lastName: "",
  mobileNoWa: "",
  mobileNoCalling: "",
  emailId: "",
  propertyConfig: "",
  walkIn: false,
  preferredDate: "",
  preferredTime: "",
};

function ManualLeadUploadModal({
  open,
  onClose,
  projects = [],
  onRawLeadCreated,
}) {
  const { userInfo } = useUser();
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);

  // Reset selected project when modal opens/closes so configs aren't stale.
  useEffect(() => {
    if (!open) {
      setSelectedProjectId("");
      setConfigs([]);
    }
  }, [open]);

  // When project changes, fetch its property configurations. Empty project
  // clears the list — keeps the dropdown coherent.
  useEffect(() => {
    if (!selectedProjectId) {
      setConfigs([]);
      return;
    }
    let cancelled = false;
    setConfigsLoading(true);
    fetchPropertyConfigurations({ projectId: selectedProjectId })
      .then((response) => {
        if (cancelled) return;
        const list =
          response?.data?.items ??
          response?.items ??
          response?.data ??
          response ??
          [];
        setConfigs(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setConfigs([]);
      })
      .finally(() => {
        if (!cancelled) setConfigsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  const projectOptions = projects.map((p) => ({
    value: String(p.projectId),
    label: p.projectName,
  }));
  const configOptions = configs
    .filter((c) => c && c.propertyConfigId !== undefined)
    .map((c) => ({
      value: String(c.propertyConfigId),
      label: `${c.projectPhase?.project?.projectName || "Project"} · ${c.configName}`,
    }));

  const handleSubmit = async (values) => {
    try {
      const project = projects.find(
        (p) => String(p.projectId) === String(values.projectId)
      );
      const customerId = project?.customerId;
      if (!customerId) {
        toast.error("Project does not have a customer ID.");
        return;
      }

      // 1. Create the user (lead).
      const userPayload = {
        firstName: values.firstName,
        lastName: values.lastName,
        mobileNoCalling: values.mobileNoCalling,
        mobileNoWa: values.mobileNoWa,
        emailId: values.emailId,
      };
      let postUrl = `/users?customerId=${customerId}&projectId=${values.projectId}`;
      if (userInfo?.persona === "CHANNEL_PARTNER") {
        postUrl += `&agentId=${userInfo.loginIdentifierDetails.customerId}`;
      }
      const userResponse = await axiosInstance.post(postUrl, userPayload);

      // 2. Link a property configuration when one was picked.
      if (values.propertyConfig) {
        const assignPayload = {
          customerId:
            userInfo?.loginIdentifierDetails?.customer?.customerId ?? customerId,
          userId: userResponse?.userId,
          personaId: 2,
          projectId: Number(values.projectId),
          status: "OPEN",
          propertyConfigId: Number(values.propertyConfig),
        };
        try {
          await axiosInstance.put("/customers/user", assignPayload);
        } catch (err) {
          console.error("Error assigning property configuration:", err);
          toast.error("Failed to assign property configuration.");
        }
      }

      // 3. Schedule a visit (or record a walk-in) when applicable.
      const hasVisitSchedule = values.preferredDate && values.preferredTime;
      if ((hasVisitSchedule || values.walkIn) && values.propertyConfig) {
        const nowIso = new Date().toISOString().replace("Z", "");
        const visitData = {
          userId: userResponse?.userId,
          customerId:
            userInfo?.loginIdentifierDetails?.customer?.customerId ?? customerId,
          agentId:
            userInfo?.loginIdentifierDetails?.customer?.customerId ??
            userInfo?.loginIdentifierDetails?.customerId,
          propertyConfigId: Number(values.propertyConfig),
          visited: Boolean(values.walkIn),
        };
        if (values.walkIn) {
          visitData.visitSchedule = null;
          visitData.visitedDateTime = [nowIso];
        } else if (hasVisitSchedule) {
          visitData.visitSchedule = `${values.preferredDate}T${values.preferredTime}:00`;
          visitData.visitedDateTime = [nowIso];
        }
        try {
          await scheduleVisit(visitData, values.walkIn);
          toast.success(
            values.walkIn
              ? "Walk-in visit recorded successfully!"
              : "Visit scheduled successfully!"
          );
        } catch (err) {
          console.error("Error scheduling visit:", err);
          toast.error("Failed to schedule visit.");
        }
      }

      toast.success("Lead added successfully!");
      onClose?.();
      onRawLeadCreated?.();
    } catch (error) {
      console.error("Error creating raw lead:", error);
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Failed to add lead.";
      toast.error(detail);
    }
  };

  return (
    <FormModal open={open} onClose={onClose} title="Add lead manually" size="xl">
      <Form
        schema={manualLeadSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        <ProjectSync onChange={(v) => setSelectedProjectId(v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-3">
          <Field
            name="projectId"
            label="Project"
            type="select"
            options={projectOptions}
            required
            className="md:col-span-2"
          />
          <Field name="firstName" label="First name" required />
          <Field name="lastName" label="Last name" required />
          <Field name="mobileNoWa" label="WhatsApp number" type="phone" required />
          <Field name="mobileNoCalling" label="Calling number" type="phone" required />
          <Field
            className="md:col-span-2"
            name="emailId"
            label="Email"
            type="email"
            required
          />

          <Field
            className="md:col-span-2"
            name="propertyConfig"
            label="Property configuration (optional)"
            type="select"
            options={configOptions}
            placeholder={
              !selectedProjectId
                ? "Select a project first"
                : configsLoading
                ? "Loading configurations…"
                : "Pick a configuration"
            }
          />

          <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">
              Site visit (optional)
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              Schedule a future visit or record this as an on-the-spot walk-in.
              Requires a property configuration above.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-3">
              <Field
                name="preferredDate"
                label="Preferred date"
                type="date"
              />
              <Field
                name="preferredTime"
                label="Preferred time"
                type="text"
                placeholder="HH:MM"
              />
              <Field name="walkIn" label="Mark as walk-in" type="checkbox" />
            </div>
          </div>
        </div>

        <FormSubmit
          loadingLabel="Creating lead…"
          secondary={
            <OutlineButton type="button" onClick={onClose}>
              Cancel
            </OutlineButton>
          }
        >
          Create lead
        </FormSubmit>
      </Form>
    </FormModal>
  );
}

/**
 * Internal helper — watches RHF's projectId so the parent modal can fetch
 * configurations for the right project. Lives inside <Form> so it can read
 * the form context cleanly without dragging the watcher into the parent.
 */
function ProjectSync({ onChange }) {
  const { watch } = useFormContext();
  const projectId = watch("projectId");
  useEffect(() => {
    onChange(projectId || "");
  }, [projectId, onChange]);
  return null;
}

export default ManualLeadUploadModal;
